import { Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, GreenText, LabelField } from 'components'
import { VehicleDto, VehicleReferenceDto } from 'dtos'
import { useEffect, useState } from 'react'
import * as controllers from 'controllers'
import InvoicingDocumentsDownloadLink from 'pages/invoicing/InvoicingDocumentsDownloadLink'

interface ReferenceDetailsCardProps {
  vehicle: VehicleDto
}

export default function ReferenceDetailsCard({ vehicle }: ReferenceDetailsCardProps) {
  const [isGettingReferenceDetails, setIsGettingReferenceDetails] = useState<boolean>(false)
  const [referenceDetails, setReferenceDetails] = useState<VehicleReferenceDto>(new VehicleReferenceDto())

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingReferenceDetails(true)
      controllers.vehicles
        .getVehicleReference(vehicle.vehicleGuid)
        .then(response => {
          setReferenceDetails(response.value)
        })
        .finally(() => {
          setIsGettingReferenceDetails(false)
        })
    }
  }, [vehicle])

  return (
    <Card variant='outlined'>
      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto'>
              <Typography fontWeight='bold' variant='h5'>
                Reference
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {isGettingReferenceDetails && (
            <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
              <Grid>
                <CircularProgress color='primary' size={50} />
              </Grid>
            </Grid>
          )}

          {!isGettingReferenceDetails && (
            <>
              <Grid item xs={12}>
                <LabelField label='Inbound Trip #' value={referenceDetails.inboundTripNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='PAPS #' value={referenceDetails.papsNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Broker Invoice #' value={referenceDetails.brokerInvoiceNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Outbound Trip #' value={referenceDetails.outboundTripNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Quickbooks Invoice #' value={referenceDetails.quickBooksInvoiceNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Invoicing Documents' value={<InvoicingDocumentsDownloadLink vehicleGuid={vehicle.vehicleGuid ?? ''} />} />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`The Inbound Trip # field is populated with the Inbound Trip # of the Inbound Trip the vehicle is associated with when the
Inbound Trip is locked. The Inbound Trip # is a link that opens the Inbound Trip details page of that Inbound Trip.

The PAPS# field is populated when the vehicle is added to a PAPS within an Inbound Trip and that PAPS is locked. The PAPS #
is a link that opens the Inbound Trip details page of that Inbound Trip.

The Broker Invoice# field is populated with the Broker Invoice # once the Broker Invoice has been OCR’d and associated with the
Inbound Trip (PAPS #) and the vehicles within that Inbound Trip. The Broker Invoice # is a link that opens the Broker Invoice
document in a new browser tab.

The Outbound Trip # field is populated with the Outbound Trip # of the Outbound Trip the vehicle is associated with when the
Outbound Trip is locked. The Outbound Trip # is a link that opens the Outbound Trip details page of that Outbound Trip.

The QuickBooks Invoice # field is populated with the QuickBooks Invoice # as entered by users under the Invoicing feature. 

The invoicing documents download link will save one PDF file to the user’s machine. That PDF contains these documents which
are associated with the vehicle: 7501, All Recall Repair Orders (these documents are uploaded using the “Repair Order Upload”
under recall campaigns where “Recall / No Recall” = “Recall”), and any uploaded documents with the following “types”: FedEx,
Replacement Label, Repairs, Vehicle Clean Up, Miscellaneous.

The Reference card is not editable by users.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
